import { LCDocumentEditorUtils } from './LCDocumentEditorUtils';
import * as CommentBlock2 from './PluginCommentBlock2';
import { HeaderVariant, RecognizedHeaderCombo } from './PluginCommentBlock2';
import { ILectioPlugin } from './Types';

export class PluginHeadingShortcuts implements ILectioPlugin {
	public Init(): void {
		CKEDITOR.plugins.add('headingshortcuts', {
			init: editorOuter => {
				editorOuter.addCommand('heading-h1', createHeaderStyleCommand('h1'));
				editorOuter.addCommand('heading-h2', createHeaderStyleCommand('h2'));
				editorOuter.addCommand('heading-h3', createHeaderStyleCommand('h3'));
				editorOuter.addCommand('heading-no', createHeaderStyleCommand('p'));
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 49 /*1*/, 'heading-h1');
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 97 /*num-1*/, 'heading-h1');
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 50 /*2*/, 'heading-h2');
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 98 /*num-2*/, 'heading-h2');
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 51 /*3*/, 'heading-h3');
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 99 /*num-3*/, 'heading-h3');
				// Kan ikke bruge ctrl-shift-n som MS Word, for chrome bruger den til incognito mode.
				// google apps bruger istedet flg.:
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 48 /*0*/, 'heading-no');
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 96 /*num-0*/, 'heading-no');

				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 78 /*N*/, 'lectioNote');
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 79 /*N*/, 'lectioIsPublic');

				// flg. er ogsaa ligesom google apps goer det.
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.SHIFT + 55 /*7*/, 'numberedlist');
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.SHIFT + 56 /*8*/, 'bulletedlist');

				//Andre genveje
				editorOuter.setKeystroke(CKEDITOR.CTRL + 81 /*Q*/, 'blockquote');
				editorOuter.setKeystroke(CKEDITOR.CTRL + 56 /*8*/, 'subscript');
				editorOuter.setKeystroke(CKEDITOR.CTRL + 57 /*9*/, 'superscript');
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 86 /*V*/, 'pastetext');
				editorOuter.setKeystroke(CKEDITOR.CTRL + 74 /*J*/, 'justifyblock');
				editorOuter.setKeystroke(CKEDITOR.CTRL + 69 /*E*/, 'justifycenter');
				editorOuter.setKeystroke(CKEDITOR.CTRL + 76 /*L*/, 'justifyleft');
				editorOuter.setKeystroke(CKEDITOR.CTRL + 82 /*R*/, 'justifyright');
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 68 /*D*/, 'showdatabasemarkup');
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 70 /*F*/, 'InsertAttachedFile');
				// NB: Man kan godt lave genvejstaster for kommandoer der ikke (altid) er defineret.
				editorOuter.setKeystroke(CKEDITOR.CTRL + CKEDITOR.ALT + 77 /*M*/, 'LectioMateriale');
			}
		});
	}
}

function createHeaderStyleCommand(tag: 'h1' | 'h2' | 'h3' | 'p'): Partial<CKEDITOR.commandDefinition> {
	const exec: CKEDITOR.commandDefinition['exec'] = editor => {
		const elementPath = editor.elementPath();
		const rootLevelElement = elementPath.block;
		if (!rootLevelElement)
			return false;

		const config = LCDocumentEditorUtils.GetEditorConfig(editor);
		let variant: HeaderVariant | null;
		if (config.enableHomeworkHeadings) {
			const suggestedVariant = GetHeaderVariantSuggestionFromDocumentContext(rootLevelElement, tag);
			const currentCombo = CommentBlock2.GetRecognizedHeaderComboForElement(rootLevelElement);
			if (currentCombo && currentCombo.tag === tag && tag !== 'p') {
				// Vi vil gerne lade brugeren cykle igennem varianterne ved at anvende genvejstasten flere gange.
				// const cycle: (HeaderVariant | null)[] = tag === 'h1' ? ['activity', null] :
				// 	tag === 'h2' ? ['homework', 'other', 'presentation', 'teacherfeedback', null] :
				// 		[];
				const cycle: (HeaderVariant | null)[] = CommentBlock2.GetRecognizedHeaderCombosForTag(tag, false)!.map(r => r.variant);
				const curidx = cycle.indexOf(currentCombo.variant);
				const nextidx = (curidx + 1) % cycle.length;
				variant = cycle[nextidx];
			}
			else
				variant = suggestedVariant;
		}
		else
			variant = null;

		const combo = CommentBlock2.GetRecognizedHeaderCombo(tag, variant);
		if (!combo || !(combo.tag === tag && combo.variant === variant))
			throw new Error('Vi har vist fundet en variant noget der ikke givet mening for ' +
				'headeren: header: ' + tag + ', variant: ' + (variant ? variant : "(null)"));

		const style = CommentBlock2.CreateLectioHeaderStyle({ element: tag }, variant, editor, false);
		if (style instanceof CKEDITOR.style)
			throw new Error('aev, vil ikke have style.');

		// editor.fire('saveSnapshot');
		style.doit();
		// editor.fire('saveSnapshot');

		return true;
	};

	return {
		exec: exec
	};
}

function GetHeaderVariantSuggestionFromDocumentContext(
	elementToChange: CKEDITOR.dom.element,
	tagToUse: CommentBlock2.HeaderTag
): CommentBlock2.HeaderVariant | null {
	const prevlist: RecognizedHeaderCombo[] = [];
	let cur = elementToChange.getPrevious();
	while (cur) {
		const combo = cur instanceof CKEDITOR.dom.element
			? CommentBlock2.GetRecognizedHeaderComboForElement(cur)
			: null;
		if (combo)
			prevlist.push(combo);
		cur = cur.getPrevious();
	}

	let variant: HeaderVariant | null;

	const pv = (() => {
		for (const combo of prevlist) {
			if (combo.tag === 'h1' && combo.variant === null)
				return '-endofactivities';
			if (combo.variant !== null)
				return combo.variant;
		}
		return '-notfound';
	})();
	switch (tagToUse) {
		case 'h1':
			switch (pv) {
				case '-endofactivities':
					variant = null;
					break;
				case '-notfound':
					variant = 'activity';
					break;
				case 'activity':
				case 'homework':
				case 'other':
				case 'presentation':
				case 'teacherfeedback':
					variant = 'activity';
					break;
				case 'resources':
					variant = null;
					break;
				default:
					const n: never = pv;
					throw new Error('Uventet prev variant for h1: ' + pv);
			}
			break;
		case 'h2':
			switch (pv) {
				case '-endofactivities':
				case 'resources':
					variant = null;
					break;
				case '-notfound':
					variant = 'homework';
					break;
				case 'activity':
					variant = 'homework';
					break;
				case 'homework':
				case 'other':
				case 'presentation':
				case 'teacherfeedback':
					variant = pv;
					break;
				default:
					const n: never = pv;
					throw new Error('Uventet prev variant for h2: ' + pv);
			}
			break;
		case 'h3':
			variant = null;
			break;
		case 'p':
			variant = null;
			break;
		default:
			const n: never = tagToUse;
			throw new Error('Uventet tag: ' + tagToUse);
	}
	return variant;
}
